/**
 * decodeTextBytes — turn a fetched file's raw bytes into text, honoring its byte-order mark.
 *
 * Why this exists: `Response.text()` always decodes as UTF-8. Spreadsheet exports from
 * Google Ads, Excel ("Unicode Text") and similar tools are often UTF-16 with a BOM, so a
 * UTF-8 decode turns every other byte into U+0000 and FileViewer's binary guard rejects a
 * perfectly good CSV ("File appears to be binary"; Nitish, Sep 26 2026, a Google Ads
 * keyword export). BOM-less files decode as UTF-8, exactly as before, so genuinely binary
 * files still carry NUL bytes and still fail the guard.
 *
 * @param {ArrayBuffer|Uint8Array} input  raw response bytes
 * @returns {{ text: string, encoding: 'utf-8'|'utf-16le'|'utf-16be' }}  text never starts with a BOM
 */
export function decodeTextBytes(input) {
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input || new ArrayBuffer(0));

  if (bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xfe) {
    return { text: decodeUtf16(bytes, true), encoding: 'utf-16le' };
  }
  if (bytes.length >= 2 && bytes[0] === 0xfe && bytes[1] === 0xff) {
    return { text: decodeUtf16(bytes, false), encoding: 'utf-16be' };
  }
  // TextDecoder strips a UTF-8 BOM by default.
  return { text: new TextDecoder('utf-8').decode(bytes), encoding: 'utf-8' };
}

// String.fromCharCode.apply takes its arguments on the stack; chunk to stay well under limits.
const CHUNK = 0x8000;

/** Decode UTF-16 code units after the 2-byte BOM. Surrogate pairs pass through as-is. */
function decodeUtf16(bytes, littleEndian) {
  const body = bytes.length - 2;
  const n = body >> 1;
  const units = new Uint16Array(n);
  for (let i = 0, p = 2; i < n; i += 1, p += 2) {
    units[i] = littleEndian ? bytes[p] | (bytes[p + 1] << 8) : (bytes[p] << 8) | bytes[p + 1];
  }
  let out = '';
  for (let i = 0; i < n; i += CHUNK) {
    out += String.fromCharCode.apply(null, units.subarray(i, i + CHUNK));
  }
  if (body % 2) out += '\ufffd'; // truncated trailing byte
  return out;
}

export default decodeTextBytes;
